import CopyableId from "@/components/common/CopyableId";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, ListChecks, Users, RefreshCw, ChevronRight,
  Pill, BookOpen, Dumbbell, Utensils, MoreHorizontal,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const CAT_META = {
  medication: { label: "Medication", icon: Pill, color: "#0B7E8C" },
  reading: { label: "Vitals Reading", icon: BookOpen, color: "#2563EB" },
  exercise: { label: "Exercise", icon: Dumbbell, color: "#7C3AED" },
  meal: { label: "Meal / Diet", icon: Utensils, color: "#D97706" },
  other: { label: "Other", icon: MoreHorizontal, color: "#64748B" },
};

function adherenceColor(pct) {
  if (pct == null) return "#94A3B8";
  if (pct >= 85) return "#15803D";
  if (pct >= 60) return "#D97706";
  return "#B91C1C";
}

function Kpi({ icon: Icon, accent, label, value, caption, testId }) {
  return (
    <div className="hah-card p-3 flex items-start gap-3" data-testid={testId}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-2xl font-bold text-[#1F2937]">{value ?? "—"}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
        {caption && <div className="text-[10px] text-[#94A3B8] mt-0.5">{caption}</div>}
      </div>
    </div>
  );
}

function AdherenceBar({ pct }) {
  const c = adherenceColor(pct);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct ?? 0}%`, background: c }} />
      </div>
      <span className="text-xs font-bold tabular-nums" style={{ color: c }}>{pct == null ? "—" : `${pct}%`}</span>
    </div>
  );
}

export default function RemindersAdherence() {
  const navigate = useNavigate();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/care-tasks/adherence", { params: { days: Number(days) } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const adherence = t.adherence;

  return (
    <div data-testid="reminders-adherence-dashboard">
      <PageHeader
        title="Reminders Adherence"
        subtitle="How reliably patients complete their scheduled care reminders across the program."
        actions={
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32" data-testid="adh-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="border-[#E5E7EB]" onClick={load} data-testid="adh-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <Kpi icon={ListChecks} accent={adherenceColor(adherence)} label="Adherence"
          value={adherence == null ? "—" : `${adherence}%`} caption={`${t.resolved || 0} resolved`} testId="adh-kpi-rate" />
        <Kpi icon={CheckCircle2} accent="#15803D" label="Completed" value={t.done} testId="adh-kpi-done" />
        <Kpi icon={XCircle} accent="#B91C1C" label="Missed" value={t.missed} testId="adh-kpi-missed" />
        <Kpi icon={Clock} accent="#D97706" label="Pending" value={t.pending} testId="adh-kpi-pending" />
        <Kpi icon={ListChecks} accent="#0B7E8C" label="Active Tasks" value={data?.active_tasks} testId="adh-kpi-tasks" />
        <Kpi icon={Users} accent="#7C3AED" label="Patients" value={data?.patients_with_tasks} testId="adh-kpi-patients" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="hah-card p-4 lg:col-span-2" data-testid="adh-trend-card">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#6B7280] mb-2">Daily Adherence Trend</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data?.trend || []}>
              <defs>
                <linearGradient id="adhFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0B7E8C" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0B7E8C" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
              <Tooltip formatter={(v) => (v == null ? "—" : `${v}%`)} />
              <Area type="monotone" dataKey="adherence" stroke="#0B7E8C" fill="url(#adhFill)" strokeWidth={2} connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="hah-card p-4" data-testid="adh-bycategory-card">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#6B7280] mb-3">By Category</div>
          <div className="space-y-3">
            {(data?.by_category || []).length === 0 && <div className="text-sm text-[#94A3B8]">No reminder data yet.</div>}
            {(data?.by_category || []).map((c) => {
              const meta = CAT_META[c.category] || CAT_META.other;
              const Icon = meta.icon;
              return (
                <div key={c.category} data-testid={`adh-cat-${c.category}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    <span className="text-xs font-semibold text-[#1F2937] flex-1">{meta.label}</span>
                    <span className="text-[10px] text-[#94A3B8]">{c.done}/{c.done + c.missed} done</span>
                  </div>
                  <AdherenceBar pct={c.adherence} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="hah-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[#F1F5F9] text-[11px] uppercase tracking-wider font-bold text-[#6B7280]">
          Patients With Most Missed Reminders
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left p-2.5">Patient</th>
              <th className="text-left p-2.5">MRN</th>
              <th className="text-left p-2.5">Missed ({data?.window_days || 30}d)</th>
              <th className="text-right p-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="p-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>}
            {!loading && (data?.top_missed_patients || []).length === 0 && (
              <tr><td colSpan={4} className="p-10 text-center text-sm text-[#6B7280]">No missed reminders in this window. 🎉</td></tr>
            )}
            {(data?.top_missed_patients || []).map((p) => (
              <tr key={p.patient_id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`adh-row-${p.patient_id}`}>
                <td className="p-2.5 font-semibold text-[#1F2937]">{p.name}</td>
                <td className="p-2.5 text-xs text-[#6B7280] font-mono"><CopyableId value={p.mrn} kind="MRN" /></td>
                <td className="p-2.5">
                  <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: "#FEE2E2", color: "#B91C1C" }}>{p.missed}</span>
                </td>
                <td className="p-2.5 text-right">
                  <Button size="sm" className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white"
                    onClick={() => navigate(`/clinical/patients/${p.patient_id}`)} data-testid={`adh-open-${p.patient_id}`}>
                    Open <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
